import { canRequestHandoff } from '@titan/shared';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useSession } from '../../session/SessionProvider.js';
import { ScreenBody, ScreenHeader } from '../../shell/Shell.js';
import { Actions, Button, Card, InlineError, InlineInfo } from '../../ui/index.js';
import { errorMessage } from '../useLoad.js';

/** The link from the handoff email lands here. Only the current superadmin can confirm. */
export function HandoffConfirm() {
  const { me, api, reload } = useSession();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [state, setState] = useState<{ done?: string; error?: string }>({});
  const [busy, setBusy] = useState(false);
  if (!me) return null;
  const allowed = canRequestHandoff(me.actor);

  const confirm = async () => {
    setBusy(true);
    setState({});
    try {
      const res = await api<{ superadmin: { name: string; email: string } }>('/org/superadmin/handoff/confirm', { method: 'POST', body: JSON.stringify({ token }) });
      setState({ done: `${res.superadmin.name} (${res.superadmin.email})` });
      await reload();
    } catch (err) {
      setState({ error: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <ScreenHeader eyebrow="administration · your organization" title="Confirm the superadmin handoff" back={{ label: 'Your organization', to: '/organization' }} detail />
      <ScreenBody narrow>
        <Card variant="form">
          {!token && <InlineError>This link is missing its confirmation token. Open the link from the email again.</InlineError>}
          {!allowed && token && <InlineError>Only the current superadmin can confirm a handoff. Sign in as the superadmin and open the link again.</InlineError>}
          {state.error && <InlineError>{state.error}</InlineError>}
          {state.done && (
            <InlineInfo>
              The superadmin role now belongs to {state.done}. You keep your account and your groups. <Link to="/organization">Back to your organization</Link>.
            </InlineInfo>
          )}
          {allowed && token && !state.done && (
            <>
              <p>Confirming hands your superadmin role to the person named in the request. You keep your account; they gain full access to every module and to management.</p>
              <Actions>
                <Button variant="destructive" disabled={busy} onClick={confirm}>
                  Confirm the handoff
                </Button>
                <Button variant="secondary" onClick={() => history.back()}>
                  Cancel
                </Button>
              </Actions>
            </>
          )}
        </Card>
      </ScreenBody>
    </>
  );
}
